import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  VenueCardGrid,
  CollectionHeadline,
  getTranslation,
  utils,
} from '@resy/ui-kitchen';
import { parameterize } from '../../../../../ui-kitchen/src/utils';
import './VenueList.scss';
import { redirectToSanitizedUrl } from '../../../../../ui-kitchen/src/utils/sanitizeTargetUrl';
import { sanitizeString } from '../../../../../ui-kitchen/src/utils/sanitizeString';
import {
  selectSearchSessionId,
} from '../../../../../ui-kitchen/src/reducers/venueSearchSlice';
import { handleMktAnalytics } from '../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../AmexMarketingUtils/constants';

const VenueList = ({
  $rootScope, badge, collectionSlug, description, eyebrow, userIsGda, hasNoResults, isMobile, isPlatinum,
  link, listId, loadNotifyModal, locationSlug, name, nameTranslationKey, showSeeMore, restaurants, selectedDate,
  selectedSeats, selectedTime, event, maxCards, isGda, deltaWifiVenuelength, isDeltaWifi, tagBooking,
  showAuthModal, favoriteVenueIds, matchMedia, isAmexMktgResyCreditEligible,
}) => {
  const getSeeMoreParams = () => {
    if (isPlatinum) {
      return {};
    }
    let params = {
      seats: selectedSeats,
      date: selectedDate,
    };

    if (isDeltaWifi) {
      const { utm } = tagBooking;
      params = {
        ...params,
        ...utm,
      };
    }
    if (listId) {
      params = {
        ...params,
        list: `collection_${listId}`,
      };
    }

    if (isAmexMktgResyCreditEligible) {
      params = {
        ...params,
        ...(selectedTime && { time: selectedTime }),
        facet: 'resy_credit_eligible_only:true',
      };
    }

    return params;
  };

  const getEyebrow = () => {
    if (isPlatinum) {
      const eyeBrowText = getTranslation({
        translationKey: 'platinum_eyebrow',
        fallback: 'For Platinum Card<sup>&reg;</sup> Members',
      });
      const sanitizedEyeBrowText = sanitizeString(eyeBrowText);

      return (
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizedEyeBrowText,
          }}
        />
      );
    }

    return eyebrow;
  };

  const getLink = () => {
    if (isPlatinum) {
      return '/platinum';
    }

    return `/cities/${locationSlug}/list/${collectionSlug}`;
  };

  const searchSessionId = useSelector(selectSearchSessionId);
  const handleSeeMoreClick = async () => {
    const pageContext = utils.getPageContext(window.location.pathname);

    if (isDeltaWifi || isAmexMktgResyCreditEligible) {
      const extraParams = {
        action_name: 'See All Click',
        ...(name && { item_name: name }),
      };
      handleMktAnalytics(event, CTA_EVENT_NAME, extraParams);
    } else {
      await event('complete search', {
        context: pageContext,
        'filter type': 'collection',
        value: name,
        searchSessionId,
      });
    }
    const query = getSeeMoreParams();
    const nonPlatinumUrl = collectionSlug ? `/cities/${locationSlug}/list/${collectionSlug}` : `/cities/${locationSlug}/search`;
    const newPath = isPlatinum ? '/platinum' : nonPlatinumUrl;
    redirectToSanitizedUrl(`${newPath}?${parameterize(query)}`, isPlatinum || isAmexMktgResyCreditEligible);
  };

  const renderHeadline = () => (((restaurants?.length > maxCards) && showSeeMore) ? (
    <CollectionHeadline
      badge={badge}
      title={name}
      translationKey={nameTranslationKey}
      description={description}
      eyebrow={getEyebrow()}
      link={getLink()}
      handleSeeMoreClick={handleSeeMoreClick}
      isGda={isGda}
      isDeltaWifi={isDeltaWifi}
      isMobile={isMobile}
      isPlatinum={isPlatinum}
      showPlatinumSeeAllLink={isPlatinum && !isMobile && restaurants?.length === 4}
    />
  ) : (
    <CollectionHeadline
      badge={badge}
      title={name}
      description={description}
      eyebrow={getEyebrow()}
      isGda={isGda}
      handleSeeMoreClick={handleSeeMoreClick}
      isMobile={isMobile}
      isPlatinum={isPlatinum}
      showPlatinumSeeAllLink={isPlatinum && !isMobile && restaurants?.length === 4}
    />
  ));

  const renderList = () => (
    <div className="VenueList__body">
      <VenueCardGrid
        $rootScope={$rootScope}
        userIsGda={userIsGda}
        event={event}
        isMobile={isMobile}
        link={link}
        list={name}
        loadNotifyModal={loadNotifyModal}
        restaurants={restaurants}
        selectedDate={selectedDate}
        selectedSeats={selectedSeats}
        isGda={isGda}
        isPlatinum={isPlatinum}
        deltaWifiVenuelength={deltaWifiVenuelength}
        isDeltaWifi={isDeltaWifi}
        tagBooking={tagBooking}
        showAuthModal={showAuthModal}
        favoriteVenueIds={favoriteVenueIds}
        description={description}
        listId={listId}
        showCarouselButtons={restaurants?.length > maxCards}
        handleSeeMoreClick={handleSeeMoreClick}
        matchMedia={matchMedia}
        isAmexMktgResyCreditEligible={isAmexMktgResyCreditEligible}
      />
    </div>
  );

  const renderHeadlineAndList = () => (
    <article className="VenueList container" role="main" aria-label={`${name} Collection`}>
      {restaurants?.length > 0 && renderHeadline()}
      {renderList()}
    </article>
  );

  const renderVenueHeadlineAndList = () => {
    if (isPlatinum) {
      return renderHeadlineAndList();
    }

    return renderHeadlineAndList();
  };

  if (hasNoResults) return null;

  return (
    <>
      {renderVenueHeadlineAndList()}
    </>
  );
};

VenueList.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  badge: PropTypes.string,
  collectionSlug: PropTypes.string,
  description: PropTypes.shape({}),
  event: PropTypes.func.isRequired,
  eyebrow: PropTypes.string,
  hasNoResults: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPlatinum: PropTypes.bool,
  loadNotifyModal: PropTypes.func.isRequired,
  locationSlug: PropTypes.string.isRequired,
  name: PropTypes.string,
  nameTranslationKey: PropTypes.string,
  selectedDate: PropTypes.string.isRequired,
  selectedSeats: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedTime: PropTypes.string,
  restaurants: PropTypes.arrayOf(PropTypes.shape({
    venue: PropTypes.shape({
      priceRange: PropTypes.number,
      rating: PropTypes.number,
      name: PropTypes.string,
      images: PropTypes.arrayOf(PropTypes.string),
      collections: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
  })),
  link: PropTypes.string,
  listId: PropTypes.number,
  maxCards: PropTypes.number,
  showSeeMore: PropTypes.bool,
  isGda: PropTypes.bool,
  userIsGda: PropTypes.bool,
  deltaWifiVenuelength: PropTypes.number,
  isDeltaWifi: PropTypes.bool,
  tagBooking: PropTypes.shape({ event_name: '', utm: PropTypes.shape({}) }),
  showAuthModal: PropTypes.func.isRequired,
  favoriteVenueIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  isAmexMktgResyCreditEligible: PropTypes.bool,
};

VenueList.defaultProps = {
  badge: '',
  collectionSlug: undefined,
  description: undefined,
  eyebrow: null,
  hasNoResults: true,
  name: '',
  nameTranslationKey: '',
  restaurants: undefined,
  selectedSeats: 2,
  selectedTime: undefined,
  listId: undefined,
  maxCards: 5,
  showSeeMore: true,
  isGda: false,
  userIsGda: false,
  deltaWifiVenuelength: 0,
  isDeltaWifi: false,
  tagBooking: {},
  isMobile: false,
  isPlatinum: false,
  link: undefined,
  isAmexMktgResyCreditEligible: false,
};

VenueList.displayName = 'VenueList';

export default VenueList;
